import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { UserTokenModel } from '../models/user-token.model';
import { environment } from '../../../environments/environment';
import { OrganService } from '../services/organ.service';

interface OTPRequest {
  mobile: string;
}

interface OTPVerifyRequest {
  mobile: string;
  code: string;
}

interface LoginRequest {
  username: string;
  password: string;
}

interface AuthResponse {
  success: boolean;
  data: {
    access: string;
    refresh: string;
    expires_in: number;
  };
}

interface RefreshTokenRequest {
  refresh: string;
}

interface RefreshTokenResponse {
  access: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = `${environment.baseApi}charity-user/auth/`;

  user = new BehaviorSubject<UserTokenModel | null>(null);
  private readonly authTokenKey = 'afraOrganizationsPortalUserData';

  private isRefreshing = new BehaviorSubject<boolean>(false);
  refreshTokenSubject = new BehaviorSubject<string | null>(null);

  private readonly organService = inject(OrganService);

  constructor(private http: HttpClient, private router: Router) {
    this.autoLogin();
  }

  requestOTP(mobile: string): Observable<any> {
    const requestBody: OTPRequest = { mobile };
    return this.http
      .post(`${this.apiUrl}otp-login/`, requestBody)
      .pipe(catchError(this.handleError));
  }

  verifyOTP(mobile: string, code: string): Observable<AuthResponse> {
    const requestBody: OTPVerifyRequest = { mobile, code };
    return this.http
      .post<AuthResponse>(`${this.apiUrl}verify/`, requestBody)
      .pipe(
        tap((response) => this.handleAuthentication(response)),
        tap(() => localStorage.setItem('isNavbarOpen', '' + true)),
        catchError(this.handleError)
      );
  }

  login(username: string, password: string): Observable<AuthResponse> {
    const requestBody: LoginRequest = { username, password };
    return this.http
      .post<AuthResponse>(`${this.apiUrl}login/`, requestBody)
      .pipe(
        tap((response) => this.handleAuthentication(response)),
        tap(() => localStorage.setItem('isNavbarOpen', '' + true)),
        catchError(this.handleError)
      );
  }

  refreshToken(): Observable<RefreshTokenResponse> {
    const currentUser = this.user.value;
    if (!currentUser || !currentUser.refreshToken) {
      return throwError(() => ({ message: 'No refresh token available' }));
    }

    const requestBody: RefreshTokenRequest = {
      refresh: currentUser.refreshToken,
    };
    return this.http
      .post<RefreshTokenResponse>(`${this.apiUrl}refresh/`, requestBody)
      .pipe(
        tap((response) => this.handleRefreshTokenResponse(response)),
        catchError(this.handleError)
      );
  }

  private handleAuthentication(response: AuthResponse): void {
    const accessToken = response.data.access;
    const refreshToken = response.data.refresh || '';

    const user = new UserTokenModel(accessToken, refreshToken);

    this.user.next(user);
    localStorage.setItem(this.authTokenKey, JSON.stringify(user));
  }

  private handleRefreshTokenResponse(response: RefreshTokenResponse): void {
    const currentUser = this.user.value;
    if (!currentUser) {
      return;
    }

    const updatedUser = new UserTokenModel(
      response.access,
      currentUser.refreshToken
    );

    this.user.next(updatedUser);
    localStorage.setItem(this.authTokenKey, JSON.stringify(updatedUser));
  }

  /** should be called in app.component.ts */
  autoLogin(): void {
    const userData = localStorage.getItem(this.authTokenKey);
    if (!userData) {
      return;
    }

    try {
      const parsedData = JSON.parse(userData);
      const loadedUser = new UserTokenModel(
        parsedData._accessToken,
        parsedData._refreshToken
      );

      if (loadedUser.token) {
        this.user.next(loadedUser);
      }
    } catch (error) {
      localStorage.removeItem(this.authTokenKey);
    }
  }

  logout(): void {
    this.router.navigate(['/account/login']);
    this.user.next(null);
    localStorage.removeItem(this.authTokenKey);
    // this.organService.clearOrgan();
  }

  private handleError(errorRes: HttpErrorResponse) {
    return throwError(() => {
      if (errorRes.status === 0) {
        return {
          status: 0,
          message: 'خطا در برقراری ارتباط با سرور',
        };
      }
      return {
        status: errorRes.status,
        message: errorRes.error?.message || 'خطایی در ورود رخ داده است.',
      };
    });
  }

  setRefreshing(state: boolean): void {
    this.isRefreshing.next(state);
  }

  setRefreshToken(token: string | null): void {
    this.refreshTokenSubject.next(token);
  }

  getRefreshState() {
    return {
      isRefreshing: this.isRefreshing.value,
      refreshToken: this.refreshTokenSubject.value,
    };
  }
}
